import { useState, useCallback, useEffect } from 'react';
import { ArrowLeft, Building2, Shield } from 'lucide-react';
import { useInvestmentFlow } from './useInvestmentFlow';
import { AmountStep } from './steps/AmountStep';
import { ReviewStep } from './steps/ReviewStep';
import { PaymentStep } from './steps/PaymentStep';
import { ProcessingStep } from './steps/ProcessingStep';
import { CompleteStep } from './steps/CompleteStep';
import type { InvestmentFlowProps, FlowStep } from './InvestmentFlow.types';
import { NAVIGABLE_STEPS } from './InvestmentFlow.utils';
import { BRAND_ASSETS } from '../../../config';
import { useUser } from '../../../contexts/UserContext';

/**
 * Multi-step investment wizard.
 *
 * Responsibilities:
 *   - Renders the active step component
 *   - Owns fade transitions between steps
 *   - Manages progress bar and header chrome
 *   - Wires navigation (back, continue, submit)
 *
 * All state and business logic live in useInvestmentFlow.
 */
export function InvestmentFlow({
  property,
  onBack,
  onComplete,
  minInvestment = 100,
  presetAmounts = [100, 500, 1000, 2500] as const,
}: InvestmentFlowProps) {
  const flow = useInvestmentFlow(property, minInvestment);
  const { canInvest, kycStatus } = useUser();
  const investCheck = canInvest();
  const [isVisible, setIsVisible] = useState(true);

  // Lock body scroll while the overlay is open so the page behind cannot scroll
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  /** Cross-fade to a new step with a 150ms opacity transition (matches AuthFlow). */
  const navigate = useCallback(
    (toStep: FlowStep) => {
      setIsVisible(false);
      setTimeout(() => {
        flow.setStep(toStep);
        setIsVisible(true);
      }, 150);
    },
    [flow]
  );

  const handleBack = useCallback(() => {
    const idx = NAVIGABLE_STEPS.indexOf(flow.step as (typeof NAVIGABLE_STEPS)[number]);
    if (idx > 0) navigate(NAVIGABLE_STEPS[idx - 1]);
    else onBack();
  }, [flow.step, navigate, onBack]);

  const handleContinue = useCallback(() => {
    const idx = NAVIGABLE_STEPS.indexOf(flow.step as (typeof NAVIGABLE_STEPS)[number]);
    if (idx >= 0 && idx < NAVIGABLE_STEPS.length - 1) {
      navigate(NAVIGABLE_STEPS[idx + 1]);
    } else if (flow.step === 'payment') {
      // Fade out PaymentStep, then submit (which internally moves step to 'processing')
      setIsVisible(false);
      setTimeout(() => {
        setIsVisible(true);
        flow.submitInvestment();
      }, 150);
    }
  }, [flow, navigate]);

  // Progress bar reflects user-decision steps only (not processing/complete)
  const progressIdx = NAVIGABLE_STEPS.indexOf(flow.step as (typeof NAVIGABLE_STEPS)[number]);
  const progressPercent =
    progressIdx >= 0 ? ((progressIdx + 1) / NAVIGABLE_STEPS.length) * 100 : 100;

  const canGoBack = flow.step !== 'processing' && flow.step !== 'complete';

  return (
    <div className="fixed inset-0 z-50 bg-background overflow-y-auto flex flex-col">
      {/* Progress bar */}
      <div
        className="sticky top-0 left-0 right-0 h-1 bg-secondary z-[60]"
        role="progressbar"
        aria-valuenow={Math.round(progressPercent)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Investment flow progress"
      >
        <div
          className="h-full bg-primary transition-all duration-500 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Header */}
      <header className="flex items-center justify-between px-4 md:px-6 py-4 md:py-6">
        <button
          type="button"
          onClick={handleBack}
          disabled={!canGoBack}
          aria-label="Go back"
          className="flex items-center gap-2 text-foreground/70 hover:text-foreground transition-colors disabled:invisible"
        >
          <ArrowLeft className="w-5 h-5" aria-hidden="true" />
        </button>

        <div className="flex items-center gap-2">
          <img
            src={BRAND_ASSETS.logo.primary}
            alt="Honeyshares"
            className="h-11 object-contain"
          />
        </div>

        {/* Spacer keeps logo centered */}
        <div className="w-8" aria-hidden="true" />
      </header>

      {/* Step content */}
      <main className="flex-1 flex items-center justify-center px-4 md:px-6 pb-16">
        <div
          className="w-full max-w-2xl transition-opacity duration-150"
          style={{ opacity: isVisible ? 1 : 0 }}
        >
          {!investCheck.canInvest && (
            <div className="text-center space-y-4 py-12">
              <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
                <Shield className="w-7 h-7 text-amber-500" />
              </div>
              <h2 className="text-xl font-semibold">Identity Verification Required</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                To comply with federal securities regulations, you must complete identity verification (KYC) before investing.
              </p>
              <p className="text-sm text-muted-foreground">
                Current status: <span className="font-medium text-amber-500 capitalize">{kycStatus || 'not started'}</span>
              </p>
            </div>
          )}

          {investCheck.canInvest && flow.step === 'amount' && (
            <AmountStep
              property={property}
              investmentAmount={flow.investmentAmount}
              shares={flow.shares}
              projectedAnnualReturn={flow.projectedAnnualReturn}
              onAmountChange={flow.setInvestmentAmount}
              onContinue={handleContinue}
              canContinue={flow.canContinueFrom('amount')}
              minInvestment={minInvestment}
              presetAmounts={presetAmounts}
            />
          )}

          {flow.step === 'review' && (
            <ReviewStep
              property={property}
              investmentAmount={flow.investmentAmount}
              shares={flow.shares}
              onConfirm={handleContinue}
            />
          )}

          {flow.step === 'payment' && (
            <PaymentStep
              investmentAmount={flow.investmentAmount}
              selectedAccountId={flow.selectedAccountId}
              onAccountSelect={flow.setSelectedAccountId}
              onSubmit={handleContinue}
              canSubmit={flow.canContinueFrom('payment')}
            />
          )}

          {flow.step === 'processing' && (
            <ProcessingStep
              error={flow.error}
              onRetry={flow.retrySubmit}
              onChangePaymentMethod={flow.goToPayment}
            />
          )}

          {flow.step === 'complete' && flow.buildResult() !== null && (
            <CompleteStep
              property={property}
              result={flow.buildResult()!}
              onViewPortfolio={() => {
                const result = flow.buildResult();
                if (result) onComplete(result);
              }}
              onBrowseMore={onBack}
            />
          )}
        </div>
      </main>
    </div>
  );
}
